import { useState, useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS } from "@/data/clinic";
import { ToothIcon } from "@/components/icons";
import { callClinic } from "@/components/CTAButtons";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const headerRef = useRef(null);

  useEffect(() => {
    const toggle = () => setOpen((p) => !p);
    window.addEventListener("sdc:toggle-menu", toggle);
    return () => window.removeEventListener("sdc:toggle-menu", toggle);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (e.target.closest('[data-testid="hero-image-menu-btn"]')) return;
      if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    if (lenis) lenis.scrollTo(`#${id}`, { offset: -64 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const reduceMotion = useReducedMotion();

  return (
    <motion.header
      ref={headerRef}
      className="fixed inset-x-0 top-0 z-50 border-b border-[#063A2F]/10 bg-[#FDFCF8]/90 backdrop-blur-xl"
      {...(reduceMotion
        ? {}
        : {
            initial: { opacity: 0, y: -24 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
          })}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-2.5"
        >
          <span className="grid h-9 w-9 place-items-center rounded-full bg-[#063A2F] text-[#2DD4BF]">
            <ToothIcon className="h-5 w-5" />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight text-[#063A2F]">
            Smart Dental <span className="italic text-[#0D9488]">Clinic</span>
          </span>
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-[#1E293B]/70 transition-colors duration-200 hover:text-[#0D9488]"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          data-testid="nav-call-btn"
          onClick={callClinic}
          className="hidden items-center gap-2 rounded-full bg-[#14B8A6] px-5 py-2.5 text-sm font-semibold text-[#063A2F] transition-colors duration-200 hover:bg-[#0F574E] hover:text-white lg:inline-flex"
        >
          <Phone className="h-4 w-4" strokeWidth={1.5} /> Call Clinic
        </button>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="text-[#063A2F] lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-[#063A2F]/10 bg-[#FDFCF8] px-6 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#1E293B]/80 transition-colors duration-200 hover:bg-[#F2FBF7] hover:text-[#0F574E]"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.header>
  );
};
